import { Purchases, Offerings, Package, PurchaseResult, ErrorCode, PurchasesError, CustomerInfo } from "@revenuecat/purchases-js";
import { logger } from "./logger.service";

export interface PurchaseResultResponse {
  success: boolean;
  error?: string;
  purchaseResult?: PurchaseResult;
}

export interface OfferingsResponse {
  success: boolean;
  error?: string;
  offerings?: Offerings;
}

export class RevenueCatService {

  /**
   * Get all available offerings
   */
  async getOfferings(): Promise<OfferingsResponse> {
    try {
      const purchases = await Purchases.getSharedInstance();
      const offerings = await purchases.getOfferings();

      if (!offerings.current && Object.keys(offerings.all).length === 0) {
        return {
          success: false,
          error: "No subscription plans available. Please check your RevenueCat dashboard configuration.",
        };
      }

      return {
        success: true,
        offerings,
      };
    } catch (error) {
      await logger.error("offerings_fetch_failed", error as Error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "RevenueCat not initialized or failed to fetch subscription plans",
      };
    }
  }

  /**
   * Purchase a product by package identifier
   */
  async purchaseProduct(packageIdentifier: string, customerEmail?: string): Promise<PurchaseResultResponse> {
    try {
      const purchases = await Purchases.getSharedInstance();
      const offerings = await purchases.getOfferings();

      if (!offerings.current && Object.keys(offerings.all).length === 0) {
        await logger.error("purchase_no_offerings", new Error("No offerings available for purchase"), { package_identifier: packageIdentifier });
        return {
          success: false,
          error: "No subscription plans available. Please check your RevenueCat dashboard configuration.",
        };
      }

      // Find the package in current offering
      let packageToPurchase: Package | null = null;
      if (offerings.current) {
        packageToPurchase = offerings.current.availablePackages.find(
          pkg => pkg.identifier === packageIdentifier,
        ) || null;
      }

      // If not found in current offering, search all offerings
      if (!packageToPurchase) {
        for (const [_offeringId, offering] of Object.entries(offerings.all)) {
          packageToPurchase = offering.availablePackages.find(
            pkg => pkg.identifier === packageIdentifier,
          ) || null;
          if (packageToPurchase) {
            break;
          }
        }
      }

      if (!packageToPurchase) {
        const availablePackages = offerings.current?.availablePackages.map(p => p.identifier) || [];
        await logger.error("purchase_package_not_found", new Error(`Package ${packageIdentifier} not found`), {
          package_identifier: packageIdentifier,
          available_packages_count: availablePackages.length,
        });
        return {
          success: false,
          error: `Subscription plan "${packageIdentifier}" not found. Available plans: ${availablePackages.join(", ")}`,
        };
      }

      // Make the purchase with detailed error tracking
      let purchaseResult: PurchaseResult;
      try {
        purchaseResult = await purchases.purchase({
          rcPackage: packageToPurchase,
          customerEmail,
        });
      } catch (purchaseError) {
        await logger.error("purchase_call_failed", purchaseError as Error, { package_identifier: packageIdentifier });

        // Error details now captured by logger service

        throw purchaseError;
      }

      return {
        success: true,
        purchaseResult,
      };
    } catch (error) {
      await logger.error("purchase_failed", error as Error, { package_identifier: packageIdentifier });

      // Handle specific RevenueCat errors
      if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
        return {
          success: false,
          error: "Purchase cancelled by user",
        };
      }

      // Handle payment configuration errors
      const errorMessage = error instanceof Error ? error.message : "Purchase failed";
      if (errorMessage.includes("elements_configuration")) {
        return {
          success: false,
          error: "Payment system configuration error. Please contact support.",
        };
      }

      // Handle general errors including RevenueCat not initialized
      return {
        success: false,
        error: errorMessage.includes("not configured") ? "RevenueCat not initialized. Please check your configuration." : errorMessage,
      };
    }
  }

  /**
   * Check if user has a specific entitlement
   */
  async checkEntitlement(entitlementIdentifier: string): Promise<boolean> {
    try {
      const purchases = await Purchases.getSharedInstance();
      const isEntitled = await purchases.isEntitledTo(entitlementIdentifier);
      return isEntitled;
    } catch (error) {
      await logger.error("entitlement_check_failed", error as Error, {
        entitlement_id: entitlementIdentifier,
      });
      return false;
    }
  }

  /**
   * Get customer info
   */
  async getCustomerInfo(): Promise<CustomerInfo> {
    try {
      const purchases = await Purchases.getSharedInstance();
      const customerInfo = await purchases.getCustomerInfo();
      return customerInfo;
    } catch (error) {
      await logger.error("customer_info_fetch_failed", error as Error);
      throw error;
    }
  }

  /**
   * Restore purchases
   */
  async restorePurchases(): Promise<CustomerInfo> {
    try {
      const purchases = await Purchases.getSharedInstance();
      const customerInfo = await purchases.getCustomerInfo();
      return customerInfo;
    } catch (error) {
      await logger.error("purchases_restore_failed", error as Error);
      throw error;
    }
  }
}
